/**
 * Vistas.
 *
 * Hooks de solo lectura sobre IndexedDB. `useLiveQuery` se re-ejecuta solo
 * cuando cambia una tabla involucrada, así que escribir un ítem repinta la
 * grilla, el panel y los totales sin que nadie tenga que orquestar nada.
 *
 * El estado de una mesa NO se lee del servidor: se deduce de las órdenes
 * locales. La tablet que está tomando el pedido siempre sabe más que el
 * backend sobre lo que acaba de pasar en esa mesa.
 */

import { useEffect, useState } from 'react';
import { useLiveQuery } from 'dexie-react-hooks';
import { db, type ItemLocal, type OrdenLocal, type PagoLocal } from './db';
import { calcularTotales, type Totales } from './repositorio';
import type { Categoria, Producto, ResumenTurno } from '../tipos/contrato';

export type EstadoMesaVista = 'libre' | 'ocupada' | 'cuenta';
/** Una cuenta con su total ya calculado, lista para pintar. */
export interface CuentaVista {
  orden: OrdenLocal;
  total: number;
  items: number;
  minutos: number;
}

export interface MesaVista {
  id: string;
  numero: string;
  nombre: string | null;
  zona: string | null;
  capacidad: number;
  esTemporal: boolean;
  estado: EstadoMesaVista;
  /** Todas las cuentas abiertas del puesto. Puede haber más de una. */
  cuentas: CuentaVista[];
  /** Suma de todas las cuentas del puesto. */
  total: number;
  /** Minutos desde que se abrió la cuenta más antigua. */
  minutos: number;
}

/** Reloj compartido. Un solo timer para toda la app en vez de uno por mesa. */
export function useAhora(intervaloMs = 20_000): number {
  const [ahora, setAhora] = useState(() => Date.now());
  useEffect(() => {
    const t = window.setInterval(() => setAhora(Date.now()), intervaloMs);
    return () => window.clearInterval(t);
  }, [intervaloMs]);
  return ahora;
}

export function useMesas(ahora: number): MesaVista[] | undefined {
  return useLiveQuery(async () => {
    const [mesas, ordenes, items] = await Promise.all([
      db.mesasRemotas.toArray(),
      db.ordenes.where('estado').anyOf('abierta', 'cuenta_solicitada').toArray(),
      db.items.toArray(),
    ]);

    const porMesa = new Map<string, OrdenLocal[]>();
    for (const o of ordenes) {
      if (!o.mesaId) continue;
      const lista = porMesa.get(o.mesaId);
      if (lista) lista.push(o);
      else porMesa.set(o.mesaId, [o]);
    }

    return mesas
      .map<MesaVista>((m) => {
        const suyas = (porMesa.get(m.id) ?? []).sort((a, b) => a.abiertaEn - b.abiertaEn);

        const cuentas = suyas.map<CuentaVista>((orden) => {
          const propios = items.filter((i) => i.ordenId === orden.id && !i.anulado);
          return {
            orden,
            total: calcularTotales(propios, orden.propinaAceptada).total,
            items: propios.reduce((s, i) => s + i.cantidad, 0),
            minutos: Math.max(0, Math.floor((ahora - orden.abiertaEn) / 60000)),
          };
        });

        // Misma regla que el trigger del backend: el puesto solo se marca
        // "cuenta pedida" si TODAS sus cuentas la pidieron. Si una sigue
        // consumiendo, el puesto sigue ocupado — de lo contrario la grilla
        // diría que la mesa está por cerrarse mientras alguien todavía pide.
        const pidieron = cuentas.filter((c) => c.orden.estado === 'cuenta_solicitada').length;
        const estado: EstadoMesaVista =
          cuentas.length === 0 ? 'libre' : pidieron === cuentas.length ? 'cuenta' : 'ocupada';

        return {
          id: m.id,
          numero: m.numero,
          nombre: m.nombre,
          zona: m.zona,
          capacidad: m.capacidad,
          esTemporal: m.esTemporal,
          estado,
          cuentas,
          total: cuentas.reduce((s, c) => s + c.total, 0),
          minutos: cuentas.length ? Math.max(...cuentas.map((c) => c.minutos)) : 0,
        };
      })
      // Los puestos temporales (barra) van al final: son de paso.
      .sort(
        (a, b) =>
          Number(a.esTemporal) - Number(b.esTemporal) ||
          a.numero.localeCompare(b.numero, 'es-CO', { numeric: true }),
      );
  }, [ahora]);
}

export interface PedidoActual {
  orden: OrdenLocal | null;
  items: ItemLocal[];
  totales: Totales;
}

const SIN_PEDIDO: PedidoActual = {
  orden: null,
  items: [],
  totales: { subtotal: 0, propina: 0, total: 0, cantidadItems: 0 },
};

export function usePedido(ordenId: string | null): PedidoActual {
  const datos = useLiveQuery(async () => {
    if (!ordenId) return SIN_PEDIDO;

    const orden = await db.ordenes.get(ordenId);
    if (!orden) return SIN_PEDIDO;

    const todos = await db.items.where('ordenId').equals(ordenId).sortBy('creadoEn');
    const items = todos.filter((i) => !i.anulado);

    return { orden, items, totales: calcularTotales(items, orden.propinaAceptada) };
  }, [ordenId]);

  return datos ?? SIN_PEDIDO;
}

export interface CatalogoVista {
  categorias: Categoria[];
  productos: Producto[];
}

export function useCatalogo(): CatalogoVista | undefined {
  return useLiveQuery(async () => {
    const [categorias, productos] = await Promise.all([
      db.categorias.orderBy('orden').toArray(),
      db.productos.toArray(),
    ]);
    return {
      categorias,
      productos: productos.sort(
        (a, b) => a.orden - b.orden || a.nombre.localeCompare(b.nombre, 'es-CO'),
      ),
    };
  }, []);
}

export const useCobros = () =>
  useLiveQuery(() => db.pagos.orderBy('creadoEn').reverse().limit(10).toArray(), []);

/**
 * Operaciones esperando subir. NO cuenta las bloqueadas: esas fueron
 * rechazadas de forma definitiva por el servidor y no van a moverse por más
 * que se espere. Mezclarlas hacía que el badge quedara clavado en un número
 * que nunca bajaba, y "hay cosas en camino" y "hay cosas rotas" son dos
 * problemas distintos para el cajero.
 */
export const usePendientesSync = () =>
  useLiveQuery(async () => {
    const todas = await db.outbox.toArray();
    return todas.filter((o) => !o.bloqueada).length;
  }, []);

/** Operaciones que el servidor rechazó y necesitan intervención. */
export const useFallidasSync = () =>
  useLiveQuery(async () => {
    const todas = await db.outbox.toArray();
    return todas.filter((o) => o.bloqueada);
  }, []);

export function useResumenTurno(
  turnoId: string | null,
  baseInicial: number,
): ResumenTurno | undefined {
  return useLiveQuery(async () => {
    if (!turnoId) return undefined;

    const [pagos, abiertas] = await Promise.all([
      db.pagos.where('turnoId').equals(turnoId).toArray(),
      db.ordenes.where('estado').anyOf('abierta', 'cuenta_solicitada').count(),
    ]);

    const vivos = pagos.filter((p) => !p.anuladoEn);
    const suma = (ms: PagoLocal['metodo'][]) =>
      vivos.filter((p) => ms.includes(p.metodo)).reduce((s, p) => s + p.monto, 0);
    
    const totalEfectivo = suma(['efectivo']);
    const totalVendido = vivos.reduce((s, p) => s + p.monto, 0);
    const totalPropinas = vivos.reduce((s, p) => s + p.propina, 0);
    const totalTarjeta = suma(['tarjeta_debito', 'tarjeta_credito']);
    const totalDigital = suma(['nequi', 'daviplata', 'transferencia']);
    const cop = (n: number) => n.toFixed(2);
    return {
      ordenesPagadas: new Set(vivos.map((p) => p.ordenId)).size,
      ordenesAbiertas: abiertas,
      totalVendido: cop(totalVendido),
      totalPropinas: cop(totalPropinas),
      totalEfectivo: cop(totalEfectivo),
      totalTarjeta: cop(totalTarjeta),
      totalDigital: cop(totalDigital),
      efectivoEsperado: cop(baseInicial + totalEfectivo),
    } satisfies ResumenTurno;
  }, [turnoId, baseInicial]);
}